import React, {
    useState,
    useEffect,
    CSSProperties,
    useMemo,
} from 'react';
import {
    motion,
    AnimatePresence,
    TargetAndTransition,
} from "framer-motion";

import * as cssClassNames from '../styles/components/Media.module.scss';

import {
    CoverType,
    getMediaDuration,
    MediaType,
    secToMs,
    setAnimationTimeout,
    ThemeType,
} from "../shared";
import Transition from "./Transition";
import MediaRenderer from './MediaRenderer';


interface MediaProps {
    className?: string;
    style?: CSSProperties;
    mediaStyle?: CSSProperties;
    mediaContainerStyle?: CSSProperties;
    media: Array<MediaType>;
    width: number | string;
    height: number | string;
    theme?: ThemeType;
    cover?: CoverType;
    dim?: boolean;
    duration?: number;
    delay?: number;
    preventInitial?: boolean;
    preventInitialSlide?: boolean;
    preventExit?: boolean;
    transitionDuration?: number;
    effect?: MediaEffect;
    forceEffectOnVideo?: boolean;
    transparent?: boolean;
    layoutId?: string;
    useInternalTiming?: boolean;
    preventTransitions?: boolean;
}

export type MediaEffect =
    'zoom-top'
    | 'zoom-center'
    | 'pan-left'
    | 'pan-right'
    | 'zoom-pan-left'
    | 'zoom-pan-right'
    | 'zoom-out-center'
    | 'rotate-out';

const effects: Record<MediaEffect, TargetAndTransition> = {
    'zoom-center': {
        scale: [1, 1.5],
    },
    'zoom-top': {
        scale: [1, 1.5],
        transformOrigin: 'top center',
    },
    'zoom-out-center': {
        scale: [1.5, 1],
    },
    'pan-left': {
        scale: [1.5, 1.5],
        x: ['10%', '-10%'],
    },
    'pan-right': {
        scale: [1.5, 1.5],
        x: ['-10%', '10%'],
    },
    'zoom-pan-left': {
        scale: [1.5, 1],
        x: ['-10%', '0%'],
    },
    'zoom-pan-right': {
        scale: [1.5, 1],
        x: ['0%', '10%'],
    },
    'rotate-out': {
        scale: [2, 1],
        rotate: ['25deg', '-10deg'],
    }
};

export default function Media(props: MediaProps) {
    const {
        layoutId,
        media,
        transparent,
        className,
        style = {},
        mediaContainerStyle = {},
        width,
        height,
        preventInitial = false,
        preventInitialSlide = false,
        preventExit = false,
        theme,
        dim = false,
        cover = 'fill',
        duration = 5,
        useInternalTiming = false,
        transitionDuration,
        mediaStyle = {},
        delay = 0,
        effect,
        preventTransitions = false,
        forceEffectOnVideo = false
    } = props;

    const [showMedia, setShowMedia] = useState(false);
    const filteredMedia = useMemo(() => media?.filter(i => i) ?? [], [media]);
    const [rendered, setRendered] = useState(false);
    const [activeMediaIndex, setActiveMediaIndex] = useState(0);

    const asset = filteredMedia[activeMediaIndex % filteredMedia.length];

    const animate = useMemo(() => {
        if (asset?.resource_type === 'video' && forceEffectOnVideo === false) {
            return {};
        }

        return effects[effect] || {};
    }, [effect, activeMediaIndex]);

    useEffect(() => {
        setAnimationTimeout(() => {
            setShowMedia(true);
        }, secToMs(delay));
    }, []);

    const playSlideshow = (index = null) => {
        let assetToPlay = index !== null ? filteredMedia[index % filteredMedia.length] : asset;
        let d = duration;

        if (useInternalTiming) {
            d = getMediaDuration(assetToPlay) || duration;
        }

        setAnimationTimeout(() => {
            setActiveMediaIndex(i => {
                playSlideshow(i + 1);
                return i + 1;
            });
        }, secToMs(d));
    }

    useEffect(() => {
        if (!showMedia) {
            return;
        }

        if (filteredMedia.length > 1) {
            playSlideshow();
        }

        setRendered(true);
    }, [showMedia]);

    return (
        <motion.div
            layoutId={layoutId}
            className={className}
            style={{
                width,
                height,
                position: 'relative',
                ...style
            }}
        >
            {
                dim && (
                    <div
                        style={{
                            top: 0,
                            left: 0,
                            zIndex: 1,
                            width: '100%',
                            height: '100%',
                            position: 'absolute',
                            backgroundColor: theme === 'dark' ? 'rgba(0,0,0,0.2)' : 'rgba(100,100,100,0.2)'
                        }}
                    />
                )
            }
            {
                showMedia && (
                    <MediaFrame
                        preventTransitions={preventTransitions}
                        delay={delay}
                        preventInitial={preventInitial}
                        preventExit={preventExit}
                        transitionDuration={transitionDuration}
                        transparent={transparent}
                        dim={dim}
                        mediaContainerStyle={mediaContainerStyle}
                        activeMediaIndex={activeMediaIndex}
                        preventInitialSlide={preventInitialSlide}
                        rendered={rendered}
                        animate={animate}
                        asset={asset}
                        mediaStyle={mediaStyle}
                        cover={cover}
                        height={height}
                    />
                )
            }
        </motion.div>
    )
}

type MediaFrameProps = {
    preventTransitions: boolean,
    delay: number,
    preventInitial: boolean,
    preventExit: boolean,
    transitionDuration?: number,
    transparent?: boolean,
    dim: boolean,
    mediaContainerStyle: CSSProperties,
    activeMediaIndex: number,
    preventInitialSlide: boolean,
    rendered: boolean,
    animate: TargetAndTransition,
    asset?: MediaType,
    mediaStyle: CSSProperties,
    cover: CoverType,
    height: number | string,
};

function MediaFrame(props: MediaFrameProps) {
    const {
        preventTransitions,
        delay,
        preventInitial,
        preventExit,
        transitionDuration,
        transparent,
        dim,
        mediaContainerStyle,
        activeMediaIndex,
        preventInitialSlide,
        rendered,
        animate,
        asset,
        mediaStyle,
        cover,
        height,
    } = props;

    const frameStyle: CSSProperties = {
        background: transparent ? 'transparent' : 'white',
        filter: dim ? 'contrast(.9) brightness(1.3)' : 'contrast(1.1)',
        width: '100%',
        height: '100%',
        position: 'relative',
        ...mediaContainerStyle,
    };

    const assetNode = (
        <motion.div
            style={{ width: '100%', height: '100%' }}
            animate={animate}
            transition={{
                duration: 15,
                ease: 'linear',
                repeatType: 'mirror',
                repeat: Infinity,
            }}
        >
            {asset ? (
                <MediaRenderer
                    className={cssClassNames.item}
                    mediaStyle={mediaStyle}
                    media={asset}
                    cover={cover}
                    height={height}
                    toApplyInternalEffect
                />
            ) : null}
        </motion.div>
    );

    if (preventTransitions) {
        return (
            <div style={frameStyle}>
                <div
                    key={activeMediaIndex}
                    style={{
                        position: 'absolute',
                        left: 0,
                        top: 0,
                        width: '100%',
                        height: '100%',
                    }}
                >
                    {assetNode}
                </div>
            </div>
        );
    }

    return (
        <Transition
            variant={'wipe-y'}
            delay={delay}
            preventInitial={preventInitial}
            preventExit={preventExit}
            duration={transitionDuration}
        >
            <div style={frameStyle}>
                <AnimatePresence>
                    <Transition
                        duration={transitionDuration}
                        cover
                        variant={(activeMediaIndex === 0 && preventInitialSlide) ? 'wipe-x' : 'wipe-slide-x'}
                        initial={rendered}
                        preventInitial={preventInitial && activeMediaIndex === 0}
                        preventExit={preventExit}
                        key={activeMediaIndex}
                    >
                        {assetNode}
                    </Transition>
                </AnimatePresence>
            </div>
        </Transition>
    );
}
